import { useEffect, useState } from 'react';
import { useProjectStore } from '../store/projectStore';
import { ImportExportDialog } from '../panels/ImportExportDialog';

export function ProjectsView() {
    const { projects, isLoading, error, loadProjects, createProject, openProject } = useProjectStore();
    const [showCreate, setShowCreate] = useState(false);
    const [formData, setFormData] = useState({ name: '', slug: '' });
    // null = closed; 'new' = dashboard-level "import as new project"; a uuid = per-project export/restore dialog
    const [dialog, setDialog] = useState<string | 'new' | null>(null);

    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        await createProject(formData);
        setFormData({ name: '', slug: '' });
        setShowCreate(false);
    };

    const handleOpen = async (projectUuid: string) => {
        await openProject(projectUuid);
        // This will trigger navigation in the parent shell
    };

    if (isLoading) {
        return <div className="px-3 py-4 text-muted">Loading projects...</div>;
    }

    if (error) {
        return <div className="alert alert-danger m-3">{error}</div>;
    }

    return (
        <div className="px-3 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="h5">Projects</h3>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setDialog('new')}
                    >
                        <i className="ti ti-upload me-1" /> Import Bundle
                    </button>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setShowCreate(!showCreate)}
                    >
                        <i className="ti ti-plus me-1" /> New Project
                    </button>
                </div>
            </div>

            {showCreate && (
                <form onSubmit={handleCreate} className="card mb-3 p-3">
                    <div className="mb-2">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Project name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                    </div>
                    <div className="mb-2">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Slug (unique identifier)"
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            required
                        />
                    </div>
                    <div className="d-flex gap-2">
                        <button type="submit" className="btn btn-sm btn-success">
                            Create
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => setShowCreate(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            <div className="list-group">
                {projects.map((project) => (
                    <div key={project.uuid} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="mb-1">{project.name}</h6>
                                <small className="text-muted">{project.slug}</small>
                            </div>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setDialog(project.uuid)}
                                >
                                    Export / Import
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleOpen(project.uuid)}
                                >
                                    Open
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {projects.length === 0 && !showCreate && (
                <div className="alert alert-info">No projects yet. Create one to get started!</div>
            )}

            {dialog === 'new' && (
                <ImportExportDialog onImportComplete={loadProjects} onClose={() => setDialog(null)} />
            )}

            {dialog && dialog !== 'new' && (
                <ImportExportDialog
                    projectUuid={dialog}
                    restoreIntoProjectUuid={dialog}
                    onImportComplete={loadProjects}
                    onClose={() => setDialog(null)}
                />
            )}
        </div>
    );
}
